import { api } from '../lib/api-client.js';

import type {
  CompleteReminderInput,
  CreateReminderInput,
  CreateReminderNoteInput,
  ReminderStatus,
  SnoozeReminderInput,
  UpdateReminderInput,
} from '@sitewise/shared';

export interface ReminderAssignee {
  userId: string;
  name: string | null;
  email: string | null;
}

export interface ReminderItem {
  id: string;
  title: string;
  description: string | null;
  entityType: string;
  entityId: string;
  priority: 'low' | 'medium' | 'high';
  status: ReminderStatus;
  dueAt: string;
  completedAt: string | null;
  cancelledAt: string | null;
  createdAt: string;
  updatedAt: string;
  assignees: ReminderAssignee[];
}

export interface ReminderSummary {
  counts: Record<ReminderStatus, number>;
  mineDueToday: number;
  mineOverdue: number;
  topOverdue: number;
}

export interface ReminderFilters {
  status?: ReminderStatus[];
  mine?: boolean;
  entityType?: string;
  entityId?: string;
  dueBefore?: string;
  dueAfter?: string;
  q?: string;
  limit?: number;
}

function buildQuery(filters: ReminderFilters): string {
  const params = new URLSearchParams();
  if (filters.status && filters.status.length > 0) params.set('status', filters.status.join(','));
  if (filters.mine !== undefined) params.set('mine', String(filters.mine));
  if (filters.entityType) params.set('entityType', filters.entityType);
  if (filters.entityId) params.set('entityId', filters.entityId);
  if (filters.dueBefore) params.set('dueBefore', filters.dueBefore);
  if (filters.dueAfter) params.set('dueAfter', filters.dueAfter);
  if (filters.q) params.set('q', filters.q);
  if (filters.limit) params.set('limit', String(filters.limit));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function listReminders(filters: ReminderFilters = {}): Promise<{ reminders: ReminderItem[] }> {
  return api(`/reminders${buildQuery(filters)}`);
}

export async function fetchReminderSummary(): Promise<ReminderSummary> {
  return api('/reminders/summary');
}

export async function createReminder(input: CreateReminderInput): Promise<{ reminder: ReminderItem }> {
  return api('/reminders', { method: 'POST', body: input });
}

export async function updateReminder(
  reminderId: string,
  input: UpdateReminderInput,
): Promise<{ reminder: ReminderItem }> {
  return api(`/reminders/${reminderId}`, { method: 'PATCH', body: input });
}

export async function completeReminder(
  reminderId: string,
  input: CompleteReminderInput = { done: true },
): Promise<{ reminder: ReminderItem }> {
  return api(`/reminders/${reminderId}/complete`, { method: 'POST', body: input });
}

export async function cancelReminder(reminderId: string): Promise<{ reminder: ReminderItem }> {
  return api(`/reminders/${reminderId}/cancel`, { method: 'POST' });
}

export async function snoozeReminder(
  reminderId: string,
  input: SnoozeReminderInput,
): Promise<{ reminder: ReminderItem }> {
  return api(`/reminders/${reminderId}/snooze`, { method: 'POST', body: input });
}

export interface ReminderNoteItem {
  id: string;
  reminderId: string;
  body: string;
  uploadId: string | null;
  createdAt: string;
  createdBy: { userId: string | null; name: string | null };
}

export async function fetchReminder(
  reminderId: string,
): Promise<{ reminder: ReminderItem; notes: ReminderNoteItem[] }> {
  return api(`/reminders/${reminderId}`);
}

export async function listReminderNotes(
  reminderId: string,
): Promise<{ notes: ReminderNoteItem[] }> {
  return api(`/reminders/${reminderId}/notes`);
}

export async function addReminderNote(
  reminderId: string,
  input: CreateReminderNoteInput,
): Promise<{ note: ReminderNoteItem }> {
  return api(`/reminders/${reminderId}/notes`, { method: 'POST', body: input });
}
